"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.methods = exports.initialState = exports._initialState = void 0;
const monocle_ts_1 = require("monocle-ts");
const fp = require("fp-ts");
const Actions = require("../../actionTypes/project/permissions");
const permissions_1 = require("../../utils/permissions");
const _folderTreeReducer = require("../../modules/files/tree/reducers");
const _propertiesReducer = require("../../modules/files/crud/reducers");
const folderTreeReducer = _folderTreeReducer.default('permissions', 'NO_INHERIT_SELECTION');
const propertiesReducer = _propertiesReducer.default('permissions');
//#region STATE
const currentPermissionsState = {
    error: null,
    permissions: [],
    status: 'STANDBY'
};
exports._initialState = {
    collapsed: [],
    error: undefined,
    projectId: -1,
    saveStatus: 'STANDBY',
    searchString: '',
    status: 'STANDBY',
    updateStatus: 'STANDBY',
    currentPermissions: Object.assign({}, currentPermissionsState),
    notifications: undefined,
    modifiedNotifications: undefined,
    folderNotifications: undefined,
    folderTree: folderTreeReducer.initialState,
    filesFolderProperties: propertiesReducer.initialState,
    includeSubfolders: true,
    processed: {},
    processing: false,
    changes: []
};
//#endregion
const initialState = () => JSON.parse(JSON.stringify(exports._initialState));
exports.initialState = initialState;
let methods = {};
exports.methods = methods;
//#endregion
//#region LENSES
const collapsedLens = monocle_ts_1.Lens.fromProp('collapsed');
const changesLens = monocle_ts_1.Lens.fromProp('changes');
const processedLens = monocle_ts_1.Lens.fromProp('processed');
const processingLens = monocle_ts_1.Lens.fromProp('processing');
const projectIdLens = monocle_ts_1.Lens.fromProp('projectId');
const saveStatusLens = monocle_ts_1.Lens.fromProp('saveStatus');
const searchStringLens = monocle_ts_1.Lens.fromProp('searchString');
const errorLens = monocle_ts_1.Lens.fromProp('error');
const toggleIncludeSubfoldersLens = monocle_ts_1.Lens.fromProp('includeSubfolders');
// Current permissions
const currentPermissionsErrorLens = monocle_ts_1.Lens.fromPath(['currentPermissions', 'error']);
const currentPermissionsPermissionsLens = monocle_ts_1.Lens.fromPath(['currentPermissions', 'permissions']);
const currentPermissionsStatusLens = monocle_ts_1.Lens.fromPath(['currentPermissions', 'status']);
//#endregion
function resolvePermissions(permission, status, newFolder) {
    if (permission === permissions_1.ACL_DELETE && status) {
        return (permissions_1.ACL_READ |
            permissions_1.ACL_WRITE |
            permissions_1.ACL_DELETE |
            (newFolder ? permissions_1.ACL_NEW_FOLDER : permissions_1.ACL_PERM_NONE));
    }
    else if ((permission === permissions_1.ACL_WRITE && status) ||
        (permission === permissions_1.ACL_DELETE && !status)) {
        return permissions_1.ACL_READ | permissions_1.ACL_WRITE | (newFolder ? permissions_1.ACL_NEW_FOLDER : permissions_1.ACL_PERM_NONE);
    }
    else if ((permission === permissions_1.ACL_READ && status) ||
        (permission === permissions_1.ACL_WRITE && !status)) {
        return permissions_1.ACL_READ;
    }
    return permissions_1.ACL_PERM_NONE;
}
//#region ACTIONS
methods[Actions.CLEAR_STATE] = () => (0, exports.initialState)();
methods[Actions.COLLAPSE_USER_GROUP_ROW] = (state, action) => {
    if (action.payload.isCollapsed) {
        return collapsedLens.modify((x) => fp.function.concat(x, action.payload.groups))(state);
    }
    return collapsedLens.modify((list) => list.filter((x) => !action.payload.groups.includes(x)))(state);
};
methods[Actions.CHANGE_SEARCH_STRING] = (state, action) => searchStringLens.set(action.payload)(state);
methods[Actions.FETCH_PROJECT_FOLDER_PERMISSIONS_REQUEST] = (state, action) => {
    return fp.function.compose(projectIdLens.set(action.payload.projectId), processingLens.set(true), currentPermissionsStatusLens.set('FETCHING'))(state);
};
methods[Actions.FETCH_PROJECT_FOLDER_PERMISSIONS_SUCCESS] = (state, action) => {
    return fp.function.compose(currentPermissionsStatusLens.set('FETCHED'), currentPermissionsPermissionsLens.set(action.payload))(state);
};
methods[Actions.FETCH_PROJECT_FOLDER_PERMISSIONS_FAILURE] = (state, action) => {
    return fp.function.compose(currentPermissionsErrorLens.set(action.payload), processingLens.set(true), currentPermissionsStatusLens.set('FAILED'))(state);
};
methods[Actions.PROJECT_FOLDER_PERMISSIONS_RESET] = (state) => {
    return fp.function.compose(saveStatusLens.set('STANDBY'), processingLens.set(true), changesLens.set([]))(state);
};
methods[Actions.SAVE_PERMISSIONS_REQUEST] = (state) => saveStatusLens.set('SAVING')(state);
methods[Actions.SAVE_PERMISSIONS_SUCCESS] = (state) => fp.function.compose(saveStatusLens.set('SAVED'), currentPermissionsPermissionsLens.set(state.currentPermissions.permissions.map((permission) => {
    const change = state.changes.find((item) => item.group === permission.group);
    return Object.assign(Object.assign({}, permission), { permissions: change ? change.permission : permission.permissions });
})), changesLens.set([]))(state);
methods[Actions.SAVE_PERMISSIONS_FAILURE] = (state, action) => fp.function.compose(saveStatusLens.set('FAILED'), errorLens.set(action.payload))(state);
//#region permissions
methods[Actions.UPDATE_PERMISSION] = (state, action) => {
    return fp.function.compose(processingLens.set(true), changesLens.modify((oldValues) => {
        return oldValues
            .filter((per) => !(per.folder === action.payload.folder &&
            action.payload.groups.includes(per.group)))
            .concat(action.payload.groups.map((group) => {
            const resolved = resolvePermissions(action.payload.permission, action.payload.status, action.payload.newFolder);
            return {
                folder: action.payload.folder,
                group: group,
                includeSubFolders: state.includeSubfolders,
                permission: resolved
            };
        }));
    }))(state);
};
methods[Actions.TOGGLE_NEW_FOLDER_PERMISSION] = (state, action) => changesLens.modify((oldValues) => action.payload.groups.reduce((res, group) => {
    const temp = oldValues.findIndex((p) => p.folder === action.payload.folder && p.group === group);
    if (temp > -1) {
        oldValues[temp].permission = action.payload.status
            ? oldValues[temp].permission | permissions_1.ACL_NEW_FOLDER
            : oldValues[temp].permission ^ permissions_1.ACL_NEW_FOLDER;
    }
    else {
        const procesesdPermissions = state.processed[action.payload.folder];
        const processedPermission = procesesdPermissions && procesesdPermissions.groups[group];
        const permissions = state.currentPermissions.permissions.find((p) => p.folder === action.payload.folder && p.group === group);
        const newPermission = processedPermission || (permissions && permissions.permissions);
        if (newPermission) {
            oldValues.push({
                folder: action.payload.folder,
                group,
                includeSubFolders: state.includeSubfolders,
                permission: action.payload.status
                    ? newPermission | permissions_1.ACL_NEW_FOLDER
                    : newPermission ^ permissions_1.ACL_NEW_FOLDER
            });
        }
    }
    return res;
}, oldValues))(state);
//#endregion
methods[Actions.SET_PERMISSIONS] = (state, action) => fp.function.compose(processingLens.set(false), processedLens.set(action.payload))(state);
methods[Actions.UPDATE_NOTIFICATIONS] = (state, action) => state;
methods[Actions.TOGGLE_INCLUDE_SUBFOLDERS] = (state) => toggleIncludeSubfoldersLens.modify((val) => !val)(state);
const folderTreeLens = monocle_ts_1.Lens.fromProp('folderTree');
const propertiesLens = monocle_ts_1.Lens.fromProp('filesFolderProperties');
const folderTreeMethods = Object.keys(folderTreeReducer.methods).reduce((prev, curr) => {
    prev[curr] = (state, action) => folderTreeLens.set(folderTreeReducer.reducer(state.folderTree, action))(state);
    return prev;
}, {});
const propertiesMethods = Object.keys(propertiesReducer.methods).reduce((prev, curr) => {
    prev[curr] = (state, action) => propertiesLens.set(propertiesReducer.reducer(state.filesFolderProperties, action))(state);
    return prev;
}, {});
exports.methods = methods = Object.assign(methods, folderTreeMethods, propertiesMethods);
//# sourceMappingURL=permissions.js.map